import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { TablePagination, usePagination } from "@/components/list-controls";
import { listServiceSchedules } from "@/lib/billing";
import { FREQ_LABEL, type BillingFrequency } from "./services";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/services_/$serviceId")({
  head: () => ({
    meta: [
      { title: "Service — Somasi Ops" },
      {
        name: "description",
        content: "What every fee schedule currently charges for one service.",
      },
    ],
  }),
  component: ServiceDetailPage,
  errorComponent: () => <div className="text-destructive">Could not load this service.</div>,
  notFoundComponent: () => <div className="text-muted-foreground">Service not found.</div>,
});

type ServiceRow = {
  id: string;
  code: string;
  name: string;
  default_fee: number;
  billing_frequency: BillingFrequency | null;
  active: boolean;
  internal_description: string | null;
};

/**
 * One service, priced by every schedule that prices it.
 *
 * The cascade read backwards. The schedule pages answer "what does this price
 * list charge for everything"; this answers "what is everyone charging for this
 * one thing", which is the question asked when a rate is being renegotiated or
 * when a catalogue price is about to change.
 *
 * Only the current rate appears. Each schedule holds a dated history of this
 * service's price, but that history belongs to the schedule — showing every
 * version of every schedule here would bury the one number each row exists to
 * report. The version count says how deep the history goes, and the row links to
 * it.
 */
function ServiceDetailPage() {
  const { serviceId } = Route.useParams();
  const navigate = useNavigate();

  const { data: service, isLoading } = useQuery({
    queryKey: ["service", serviceId],
    queryFn: async () => {
      const { data, error } = await api.services.get(serviceId, [
        "id",
        "code",
        "name",
        "default_fee",
        "billing_frequency",
        "active",
        "internal_description",
      ]);
      if (error) throw error;
      return data as ServiceRow | null;
    },
  });

  const { data: schedules = [], isLoading: schedulesLoading } = useQuery({
    queryKey: ["service_fee_schedules", serviceId],
    queryFn: () => listServiceSchedules(serviceId),
  });

  const pager = usePagination({ pageSize: 10 });
  const page = useMemo(
    () => schedules.slice(pager.offset, pager.offset + pager.pageSize),
    [schedules, pager.offset, pager.pageSize],
  );

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!service) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Service not found</AlertTitle>
        <AlertDescription>
          It may have been deleted. Go back to the{" "}
          <Link to="/services" className="underline">
            service catalogue
          </Link>
          .
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <PageHeader
        title={service.name}
        description={`${service.code} · standard fee USD ${Number(
          service.default_fee ?? 0,
        ).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
        breadcrumbs={[{ label: "Services", to: "/services" }, { label: service.code }]}
        actions={
          <Button variant="outline" onClick={() => navigate({ to: "/services" })}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to catalogue
          </Button>
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact
          label="Standard fee"
          value={`USD ${Number(service.default_fee ?? 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}`}
        />
        <Fact
          label="Default frequency"
          value={service.billing_frequency ? FREQ_LABEL[service.billing_frequency] : "—"}
        />
        <Fact
          label="Priced by"
          value={`${schedules.length} fee ${schedules.length === 1 ? "schedule" : "schedules"}`}
        />
        <Fact label="Standard status" value={service.active ? "Active" : "Inactive"} />
      </div>

      <Alert className="mb-4">
        <AlertTitle>Today's rates</AlertTitle>
        <AlertDescription>
          Each row is what that schedule charges <strong>today</strong> — the dated set in force
          now, which is not always its newest: a set that starts next month is not billing yet and
          is not shown here. A schedule whose current set does not price this service is left out
          entirely, because today it does not price it. A company reading a schedule that does not
          appear here is billed the standard fee of USD{" "}
          {Number(service.default_fee ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          . Open a schedule to see what it charged in earlier periods.
        </AlertDescription>
      </Alert>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fee schedule</TableHead>
              <TableHead className="w-28">State</TableHead>
              <TableHead className="w-40">Frequency</TableHead>
              <TableHead className="w-56">In force</TableHead>
              <TableHead className="w-28 text-right">Versions</TableHead>
              <TableHead className="w-40 text-right">Price</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedulesLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : page.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  No fee schedule prices this service. Every company is billed the standard fee.
                </TableCell>
              </TableRow>
            ) : (
              page.map((row) => (
                <TableRow
                  key={row.fee_schedule_id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: "/fee-schedules/$scheduleId/versions/$effectiveFrom",
                      params: {
                        scheduleId: row.fee_schedule_id,
                        effectiveFrom: row.effective_from,
                      },
                    })
                  }
                >
                  <TableCell className="font-medium">
                    {/* Underlined because the whole row opens this schedule's dated set. */}
                    <span className="underline underline-offset-2">{row.schedule_name}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={row.active ? "default" : "outline"}>
                      {row.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {row.billing_frequency
                      ? FREQ_LABEL[row.billing_frequency as BillingFrequency]
                      : "(inherit)"}
                  </TableCell>
                  <TableCell className="tabular-nums text-sm whitespace-nowrap">
                    {row.effective_from} → {row.effective_to ?? "open"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                    {row.version_count}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    USD {Number(row.fee).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        pagination={pager}
        total={schedules.length}
        loading={schedulesLoading}
        unit="schedule"
      />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}
